import { useCallback, useMemo, useRef } from "react";
import { ColumnQuery, selectColumnCards } from "./board-selectors";
import { BoardStore } from "./board-store";
import { parseJql } from "./jql";
import { rankBetween } from "./rank";
import { IssueCard } from "./types";
import { useBoardStoreSnapshot } from "./use-board-store";
import { ColumnFilters, ColumnIssuesState, useColumnIssues } from "./use-column-issues";

// Maps the column's combined filter object to the selector query (chips + free text + parsed JQL).
function toQuery(filters: ColumnFilters): ColumnQuery {
  return {
    filters: {
      assignee: filters.assignee ?? null,
      priority: filters.priority ?? null,
      type: filters.type ?? null
    },
    q: filters.q,
    jql: filters.jql ? parseJql(filters.jql) : null
  };
}

const NOOP = (): void => {};

// The board column's data source. By default it derives the column from the in-memory store
// (zero network on any filter/JQL/group change). It falls back to the per-column server query
// ONLY when the store can't serve the board — i.e. the working set was truncated ("fallback")
// or a cold sync failed ("error"). Returns the exact same ColumnIssuesState shape either way,
// so the column component and the board's drag-drop/keyboard wiring are unchanged.
export function useColumnSource(
  boardId: string,
  columnId: string,
  statusIds: string[],
  filters: ColumnFilters
): ColumnIssuesState {
  const snapshot = useBoardStoreSnapshot();
  const serverMode: boolean = snapshot.status === "fallback" || snapshot.status === "error";

  // Always called (rules of hooks); the network is gated off unless we're in server mode.
  const serverState: ColumnIssuesState = useColumnIssues(boardId, columnId, statusIds, filters, {
    enabled: serverMode
  });

  const filtersRef = useRef<ColumnFilters>(filters);
  filtersRef.current = filters;
  // Stable dependency for the column's status set (arrays are referentially unstable per render).
  const statusIdsKey: string = statusIds.join(",");
  const statusIdsRef = useRef<string[]>(statusIds);
  statusIdsRef.current = statusIds;

  // Memoized on (working-set version via issues ref, status set, filters) so `items` keeps a stable
  // identity between unrelated renders — the parent reports items up on change, so a fresh array
  // every render would loop (the same hazard the server path guards against). `filters` is the
  // page's memoized columnFilters, so it's referentially stable until its content changes.
  const items: IssueCard[] = useMemo(
    () => selectColumnCards(snapshot.issues, statusIds, toQuery(filters)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statusIdsKey stands in for statusIds
    [snapshot.issues, statusIdsKey, filters]
  );

  const addOptimistic = useCallback((card: IssueCard): void => BoardStore.upsertOptimistic(card), []);
  const removeOptimistic = useCallback((id: string): void => BoardStore.removeOptimistic(id), []);
  const patchOptimistic = useCallback(
    (id: string, patch: Partial<IssueCard>): void => BoardStore.patchOptimistic(id, patch),
    []
  );
  // Place a (cross-column) card at `index` in THIS column: give it a rank between the live
  // neighbours there — read live, not from a stale render closure — then upsert. The card already
  // carries its resolved destination status (board-page computes it for multi-status columns), so
  // we preserve card.statusId rather than overwrite it. The move's reconcile corrects the rank.
  const insertOptimistic = useCallback((card: IssueCard, index: number): void => {
    const live = BoardStore.getSnapshot();
    const column: IssueCard[] = selectColumnCards(
      live.issues,
      statusIdsRef.current,
      toQuery(filtersRef.current)
    ).filter((entry: IssueCard) => entry.id !== card.id);
    const before: IssueCard | undefined = index > 0 ? column[index - 1] : undefined;
    const after: IssueCard | undefined = column[index];
    const rank: string = rankBetween(before?.rank ?? null, after?.rank ?? null);
    BoardStore.upsertOptimistic({ ...card, rank });
  }, []);
  const reload = useCallback((): void => {
    void BoardStore.sync();
  }, []);

  const storeState: ColumnIssuesState = useMemo(
    () => ({
      items,
      total: items.length,
      loading: snapshot.status === "syncing",
      loadingMore: false, // the store path holds the whole working set — never paginates
      hasMore: false,
      hasLoaded: snapshot.status === "ready",
      error: false,
      loadMore: NOOP,
      reload,
      addOptimistic,
      removeOptimistic,
      insertOptimistic,
      patchOptimistic
    }),
    [items, snapshot.status, reload, addOptimistic, removeOptimistic, insertOptimistic, patchOptimistic]
  );

  return serverMode ? serverState : storeState;
}
